import { useId, useState, type ReactNode } from 'react'
import {
  ChevronDown,
  ChevronUp,
  Download,
  Eye,
  FileText,
  Folder,
  Trash2,
} from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import { FileKindBadge } from '../../utils/fileKind'
import { getFileKindLabel } from '../../utils/fileKindMeta'
import { resolveAttachmentId } from '../../utils/residentDecoupling'
import type { OldStatementRow } from '../../types'
import type { LibertyInternalDocumentsSectionProps } from '../../types'
import type { ResidentDetailResponse } from '../../types/resident'
import { LibertyInternalFileUploadRow } from './LibertyInternalFileUploadRow'

/** ~5 file rows visible; remaining files scroll inside the drawer. */
const DOC_LIST_MAX_H = 'max-h-[calc(5*4.75rem)]'

function normalizeOldStatementFromResident(
  residentDetail: ResidentDetailResponse | null | undefined,
): OldStatementRow[] {
  const raw = residentDetail?.document?.old_statement as
    | OldStatementRow[]
    | Record<string, OldStatementRow>
    | undefined
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  return Object.values(raw).filter(Boolean) as OldStatementRow[]
}

function formatUploadDate(value?: string | number | null): string {
  if (value === undefined || value === null || value === '') return ''
  const date =
    typeof value === 'number'
      ? new Date(value > 1e12 ? value : value * 1000)
      : new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatFileSize(bytes?: unknown): string {
  const n = typeof bytes === 'number' ? bytes : Number(bytes)
  if (!Number.isFinite(n) || n <= 0) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function buildMetaLine(parts: Array<string | undefined | null>): string {
  return parts.map((p) => String(p || '').trim()).filter(Boolean).join(' · ')
}

/**
 * Mirrors Angular's "Liberty Documents" + "Reimbursement Settlement Statement
 * Document" sections on the Liberty Internal tab — collapsible drawers with
 * ~5 visible file rows and scroll for the rest.
 */
export function LibertyInternalDocumentsSection({
  residentDetail,
  oldStatementRows,
  libertyDocuments,
  isExternalInsurance,
  libertyUploading,
  statementUploading,
  onUploadLibertyDocuments,
  onRemoveLibertyDocument,
  onViewLibertyDocument,
  onDownloadLibertyDocument,
  onUploadStatementDocument,
  onRemoveStatementDocument,
  onViewStatementDocument,
  onDownloadStatementDocument,
  onOversizedFile,
  uploadsDisabled = false,
  removesDisabled = false,
}: LibertyInternalDocumentsSectionProps) {
  const oldStatement: OldStatementRow[] =
    oldStatementRows != null ? oldStatementRows : normalizeOldStatementFromResident(residentDetail)

  const uploadBusy = libertyUploading || statementUploading
  const fileInputDisabled = uploadBusy || uploadsDisabled
  const viewLiberty = onViewLibertyDocument ?? onDownloadLibertyDocument
  const viewStatement = onViewStatementDocument ?? onDownloadStatementDocument

  return (
    <div
      className="mt-6 flex flex-col gap-4"
      data-testid="resident-liberty-internal-documents"
    >
      {!isExternalInsurance ? (
        <DocumentDrawerCard
          title="Liberty Documents"
          icon={<Folder className="h-4 w-4" strokeWidth={2.25} />}
          fileCount={libertyDocuments.length}
          testId="resident-liberty-documents-section"
          headerAction={
            <LibertyInternalFileUploadRow
              multiple
              variant="addButton"
              disabled={fileInputDisabled}
              uploading={libertyUploading}
              aria-label="Upload Liberty documents"
              data-testid="resident-liberty-documents-input"
              onFilesSelected={(files) => {
                void onUploadLibertyDocuments(files)
              }}
              onOversizedFile={onOversizedFile}
            />
          }
        >
          <div
            className="border-b border-[#e8eef5] bg-[#fffbeb] px-4 py-2.5 text-xs font-medium text-black"
            role="alert"
          >
            Use CTRL (Windows) / CMD (Apple) + Click to select multiple files to upload at one time.
          </div>
          {libertyDocuments.length ? (
            <ul
              className={cn(
                'divide-y divide-slate-100 overflow-y-auto overscroll-contain',
                DOC_LIST_MAX_H,
              )}
              data-testid="resident-liberty-documents-list"
            >
              {libertyDocuments.map((doc) => {
                const docKey = String(doc.id || doc.attachment_path)
                const removeId = doc.id ?? (doc as Record<string, unknown>).attachment_id
                const name = String(doc.attachment_name || doc.attachment_path || 'Document')
                const size = formatFileSize(
                  doc.file_size ?? doc.size ?? doc.filesize ?? doc.attachment_size,
                )
                const uploaded = formatUploadDate(doc.created_at as string | number | undefined)
                const by = String(
                  (doc.uploaded_by as string) ||
                    (doc.created_by as string) ||
                    (doc.user_name as string) ||
                    '',
                ).trim()
                const meta = buildMetaLine([
                  getFileKindLabel(name),
                  size,
                  uploaded
                    ? `Uploaded ${uploaded}${by ? ` by ${by}` : ''}`
                    : by
                      ? `Uploaded by ${by}`
                      : '',
                ])
                return (
                  <DocumentFileRow
                    key={docKey}
                    testId={`resident-liberty-documents-row-${docKey}`}
                    name={name}
                    meta={meta}
                    canRemove={removeId != null && removeId !== '' && !removesDisabled}
                    onView={() => viewLiberty(doc)}
                    onDownload={() => onDownloadLibertyDocument(doc)}
                    onRemove={() => onRemoveLibertyDocument(removeId as string | number)}
                  />
                )
              })}
            </ul>
          ) : (
            <p
              className="px-4 py-8 text-center text-sm font-medium text-slate-500"
              data-testid="resident-liberty-documents-empty"
            >
              No Liberty documents uploaded yet.
            </p>
          )}
        </DocumentDrawerCard>
      ) : null}

      <DocumentDrawerCard
        title="Reimbursement Settlement Statement"
        icon={<FileText className="h-4 w-4" strokeWidth={2.25} />}
        fileCount={oldStatement.length}
        testId="resident-settlement-statement-section"
        headerAction={
          <LibertyInternalFileUploadRow
            variant="addButton"
            disabled={fileInputDisabled}
            uploading={statementUploading}
            aria-label="Upload reimbursement settlement statement"
            data-testid="resident-settlement-statement-input"
            onFilesSelected={(files) => {
              const file = files[0]
              if (file) onUploadStatementDocument(file)
            }}
            onOversizedFile={onOversizedFile}
          />
        }
      >
        {oldStatement.length ? (
          <ul
            className={cn(
              'divide-y divide-slate-100 overflow-y-auto overscroll-contain',
              DOC_LIST_MAX_H,
            )}
            data-testid="resident-settlement-statement-list"
          >
            {oldStatement.map((doc, index) => {
              const removeId = resolveAttachmentId(doc as Record<string, unknown>)
              const docKey = String(removeId || index)
              const name = String(doc.filename || doc.filepath || doc.attachment_name || 'Statement')
              const size = formatFileSize(
                doc.file_size ?? doc.size ?? doc.filesize ?? doc.attachment_size,
              )
              const uploaded = formatUploadDate(
                (doc.created_at as string | number | undefined) ??
                  (doc.filedate as string | number | undefined),
              )
              const by = String(
                (doc.uploaded_by as string) ||
                  (doc.created_by as string) ||
                  (doc.user_name as string) ||
                  '',
              ).trim()
              const meta = buildMetaLine([
                getFileKindLabel(name),
                size,
                uploaded
                  ? `Uploaded ${uploaded}${by ? ` by ${by}` : ''}`
                  : by
                    ? `Uploaded by ${by}`
                    : '',
              ])
              return (
                <DocumentFileRow
                  key={docKey}
                  testId={`resident-settlement-statement-row-${docKey}`}
                  name={name}
                  meta={meta}
                  canRemove={
                    !isExternalInsurance && removeId != null && removeId !== '' && !removesDisabled
                  }
                  onView={() => viewStatement(doc)}
                  onDownload={() => onDownloadStatementDocument(doc)}
                  onRemove={() => onRemoveStatementDocument(removeId as string | number)}
                />
              )
            })}
          </ul>
        ) : (
          <p
            className="px-4 py-8 text-center text-sm font-medium text-slate-500"
            data-testid="resident-settlement-statement-empty"
          >
            No settlement statement uploaded yet.
          </p>
        )}
      </DocumentDrawerCard>
    </div>
  )
}

function DocumentDrawerCard({
  title,
  icon,
  fileCount,
  testId,
  headerAction,
  children,
}: {
  title: string
  icon: ReactNode
  fileCount: number
  testId: string
  headerAction?: ReactNode
  children: ReactNode
}) {
  const [open, setOpen] = useState(true)
  const panelId = useId()
  const countLabel = fileCount === 1 ? '1 file' : `${fileCount} files`

  return (
    <section
      className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
      data-testid={testId}
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-[#dbe7f3] bg-[#eef6fc] px-3 py-2.5 sm:gap-3 sm:px-4 sm:py-3">
        <button
          type="button"
          data-testid={`${testId}-toggle`}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((prev) => !prev)}
          className="flex min-w-0 flex-1 items-center gap-3 text-left transition"
        >
          <span
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-[#1597ff] shadow-sm ring-1 ring-[#cfe6f8]"
            aria-hidden="true"
          >
            {icon}
          </span>
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="text-sm font-bold text-black">{title}</span>
            <span
              className="inline-flex rounded-full bg-[#d7ebfb] px-2 py-0.5 text-[11px] font-semibold text-black"
              data-testid={`${testId}-count`}
            >
              {countLabel}
            </span>
          </span>
        </button>
        {headerAction}
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
          onClick={() => setOpen((prev) => !prev)}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-white/70"
          data-testid={`${testId}-chevron`}
        >
          {open ? (
            <ChevronUp className="h-5 w-5" aria-hidden />
          ) : (
            <ChevronDown className="h-5 w-5" aria-hidden />
          )}
        </button>
      </div>
      {open ? (
        <div id={panelId} data-testid={`${testId}-panel`}>
          {children}
        </div>
      ) : null}
    </section>
  )
}

function DocumentFileRow({
  name,
  meta,
  canRemove,
  onView,
  onDownload,
  onRemove,
  testId,
}: {
  name: string
  meta: string
  canRemove: boolean
  onView: () => void
  onDownload: () => void | Promise<void>
  onRemove: () => void
  testId: string
}) {
  const [busy, setBusy] = useState<'view' | 'download' | null>(null)
  const outlineBtn =
    'inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-[#1597ff] bg-white px-2.5 text-sm font-semibold text-[#1597ff] transition hover:bg-[#eef7ff] disabled:pointer-events-none disabled:opacity-60'
  const iconBtn =
    'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#1597ff] bg-white text-[#1597ff] transition hover:bg-[#eef7ff] disabled:pointer-events-none disabled:opacity-60'
  const deleteBtn =
    'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-red-300 bg-white text-red-600 transition hover:bg-red-50'

  const run = async (actionKind: 'view' | 'download', action: () => void | Promise<void>) => {
    setBusy(actionKind)
    try {
      await Promise.resolve(action())
    } finally {
      setBusy(null)
    }
  }

  return (
    <li
      className="flex flex-col gap-3 px-3 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-4"
      data-testid={testId}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <FileKindBadge filename={name} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-black" title={name}>
            {name}
          </p>
          {meta ? (
            <p className="mt-0.5 truncate text-xs font-medium text-black/70">{meta}</p>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => void run('view', onView)}
          data-testid={`${testId}-view`}
          className={outlineBtn}
          title="View"
        >
          <Eye className="h-4 w-4" aria-hidden />
          {busy === 'view' ? 'Opening…' : 'View'}
        </button>
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => void run('download', onDownload)}
          data-testid={`${testId}-download`}
          className={iconBtn}
          title="Download"
          aria-label={`Download ${name}`}
        >
          <Download className="h-4 w-4" aria-hidden />
        </button>
        {canRemove ? (
          <button
            type="button"
            onClick={onRemove}
            data-testid={`${testId}-remove`}
            className={deleteBtn}
            title="Remove"
            aria-label={`Remove ${name}`}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
      </div>
    </li>
  )
}

export type { OldStatementRow } from '../../types'
